/**
 * ScheduleReleaseWizard
 * 3-step wizard for releasing a professional's schedule
 *
 * Step 1: Select period and preview holidays
 * Step 2: Review holiday decisions and see summary
 * Step 3: Confirm and create slots
 *
 * @version 2025.1.0
 */
import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import ScheduleReleaseStep1 from './ScheduleReleaseStep1';
import ScheduleReleaseStep2 from './ScheduleReleaseStep2';
import ScheduleReleaseStep3 from './ScheduleReleaseStep3';
import {
  scheduleReleaseService,
  ScheduleReleasePreviewResponse,
  ScheduleReleaseSummaryResponse,
  HolidayDecision,
  getDefaultDateRange,
  ScheduleRelease,
} from '../../../services/scheduleReleaseService';

interface ScheduleReleaseWizardProps {
  open: boolean;
  onClose: () => void;
  professionalId: number;
  professionalName: string;
  clinicId: number;
  clinicName: string;
  companyId?: number;
  onComplete?: (release: ScheduleRelease, slotsCreated: number) => void;
}

const STEPS = ['Selecionar Período', 'Revisar Feriados', 'Confirmar'];

const ScheduleReleaseWizard: React.FC<ScheduleReleaseWizardProps> = ({
  open,
  onClose,
  professionalId,
  professionalName,
  clinicId,
  clinicName,
  companyId,
  onComplete,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const defaultRange = getDefaultDateRange();

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1 data
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [preview, setPreview] = useState<ScheduleReleasePreviewResponse | null>(null);

  // Step 2 data
  const [holidayDecisions, setHolidayDecisions] = useState<HolidayDecision[]>([]);
  const [summary, setSummary] = useState<ScheduleReleaseSummaryResponse | null>(null);

  // Reset wizard state
  const resetWizard = useCallback(() => {
    const range = getDefaultDateRange();
    setActiveStep(0);
    setLoading(false);
    setError(null);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setPreview(null);
    setHolidayDecisions([]);
    setSummary(null);
  }, []);

  // Handle close
  const handleClose = useCallback(() => {
    resetWizard();
    onClose();
  }, [resetWizard, onClose]);

  // Step 1: Load preview
  const handleStep1Next = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const previewData = await scheduleReleaseService.preview({
        professionalId,
        clinicId,
        startDate,
        endDate,
      });

      setPreview(previewData);

      // Initialize holiday decisions (default: skip all holidays)
      const decisions = previewData.holidays.map((holiday) => ({
        holidayId: holiday.id,
        date: holiday.date,
        name: holiday.name,
        scope: holiday.scope,
        workOnHoliday: false,
      }));
      setHolidayDecisions(decisions);

      setActiveStep(1);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar preview';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [professionalId, clinicId, startDate, endDate]);

  // Step 2: Load summary
  const handleStep2Next = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const summaryData = await scheduleReleaseService.summary({
        professionalId,
        clinicId,
        startDate,
        endDate,
        holidayDecisions,
      });

      setSummary(summaryData);
      setActiveStep(2);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao calcular resumo';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [professionalId, clinicId, startDate, endDate, holidayDecisions]);

  // Step 3: Confirm and create slots
  const handleConfirm = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await scheduleReleaseService.confirm({
        professionalId,
        clinicId,
        companyId,
        startDate,
        endDate,
        holidayDecisions,
      });

      enqueueSnackbar(
        `Agenda liberada! ${result.slotsCreated} slots criados.`,
        { variant: 'success' }
      );

      if (onComplete) {
        onComplete(result.release, result.slotsCreated);
      }

      handleClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao liberar agenda';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [
    professionalId,
    clinicId,
    companyId,
    startDate,
    endDate,
    holidayDecisions,
    enqueueSnackbar,
    onComplete,
    handleClose,
  ]);

  // Handle back
  const handleBack = useCallback(() => {
    setError(null);
    setActiveStep((prev) => Math.max(0, prev - 1));
  }, []);

  // Handle holiday decision change
  const handleHolidayDecisionChange = useCallback((holidayId: number, workOnHoliday: boolean) => {
    setHolidayDecisions((prev) =>
      prev.map((decision) =>
        decision.holidayId === holidayId
          ? { ...decision, workOnHoliday }
          : decision
      )
    );
  }, []);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Liberar Agenda</Typography>
        <Typography variant="body2" color="text.secondary">
          {professionalName} - {clinicName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {activeStep === 0 && (
              <ScheduleReleaseStep1
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
              />
            )}

            {activeStep === 1 && preview && (
              <ScheduleReleaseStep2
                preview={preview}
                holidayDecisions={holidayDecisions}
                onHolidayDecisionChange={handleHolidayDecisionChange}
              />
            )}

            {activeStep === 2 && summary && (
              <ScheduleReleaseStep3
                summary={summary}
                holidayDecisions={holidayDecisions}
              />
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>

        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Voltar
          </Button>
        )}

        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={handleStep1Next}
            disabled={loading || !startDate || !endDate}
          >
            Próximo
          </Button>
        )}

        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={handleStep2Next}
            disabled={loading}
          >
            Próximo
          </Button>
        )}

        {activeStep === 2 && (
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirm}
            disabled={loading}
          >
            Confirmar e Liberar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleReleaseWizard;
